import { ChildProfile, Story } from '../types';

async function request(path: string, options: RequestInit = {}) {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Die Daten konnten nicht geladen werden.');
  return payload;
}

function mapStory(raw: any): Story {
  return {
    id: raw.id,
    title: raw.title,
    pages: (raw.scenes || []).map((scene: any, index: number) => ({
      pageNumber: index + 1,
      content: scene.text,
      illustrationPrompt: scene.imagePrompt,
      imageUrl: raw.images?.[index]?.url || '',
      imagePending: raw.images?.[index]?.pending === true,
      imageFailed: raw.images?.[index]?.failed === true,
      imageErrorCode: raw.images?.[index]?.errorCode || undefined,
    })),
    createdAt: raw.createdAt,
    isSaved: true,
    isSafe: true,
    authorId: raw.userId || 'current-user',
    isPaid: true,
    audioUrl: raw.audioUrl || null,
    demo: Boolean(raw.demo),
    language: raw.language,
  };
}

function mapPrimaryChildProfile(payload: any): ChildProfile | null {
  const stored = payload?.profiles?.[0];
  if (!stored) return null;
  const age = stored.ageGroup === '3-5' ? 4 : stored.ageGroup === '6-8' ? 7 : 10;
  return { name: stored.name, age, voice: 'Kind', language: stored.language || 'de' };
}

export class DBService {
  static async saveStory(_story: Story): Promise<void> {
    // Neue Geschichten werden bereits atomar auf dem Server gespeichert.
  }

  static async getAllStories(_userId: string): Promise<Story[]> {
    const portal = await this.getPortal();
    return portal.stories;
  }

  // A profile and its bookshelf belong to one Lumi account. Reading both from
  // the same response prevents one device from briefly showing an old local
  // profile while another device already has the newer account data.
  static async getPortal(): Promise<{ profile: ChildProfile | null; stories: Story[] }> {
    const payload = await request('/api/portal');
    return {
      profile: mapPrimaryChildProfile(payload),
      stories: (payload.stories || []).map(mapStory),
    };
  }

  static async getStory(id: string): Promise<Story> {
    const payload = await request(`/api/stories/${encodeURIComponent(id)}`);
    return mapStory(payload.story);
  }

  static async deleteStory(id: string): Promise<void> {
    await request(`/api/stories/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async startPrintBookCheckout(storyId: string): Promise<string> {
    const payload = await request('/api/payments/print-book-checkout', {
      method: 'POST',
      body: JSON.stringify({ storyId }),
    });
    if (!payload.url) throw new Error('Stripe konnte die Buch-Zahlungsseite nicht öffnen.');
    return payload.url;
  }

  static async getPrimaryChildProfile(): Promise<ChildProfile | null> {
    const portal = await this.getPortal();
    return portal.profile;
  }

  static async saveChildProfile(profile: ChildProfile): Promise<void> {
    const ageGroup = profile.age <= 5 ? '3-5' : profile.age <= 8 ? '6-8' : '9-11';
    await request('/api/profiles', {
      method: 'POST',
      body: JSON.stringify({ name: profile.name, ageGroup, avatar: 'Stern', language: profile.language }),
    });
  }
}
